import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'ui/Button';
import { InputSearch } from 'ui/InputSearch';
import { InfoEmployeds } from 'components/InfoEmployeds';
import { getFormatPhone } from 'utils/getFotmatPhone';
import close from './img/iconClose.svg';
import styles from './EmployeeSelection.module.scss';
import { Group, Loader, Switch } from '@mantine/core';
import { ReactComponent as CheckIcon } from './img/check.svg';
import { ReactComponent as AlertIcon } from './img/alert.svg';
import { ReactComponent as WarningIcon } from './img/iconWarning.svg';
import { ReactComponent as IconError } from './img/iconError.svg';
import { ReactComponent as InfoIcon } from './img/iconInfo.svg';
import { ReactComponent as IconCalendar } from '../OrderExecutor/img/iconCalendar.svg';
import { ReactComponent as IconWork } from '../OrderExecutor/img/iconWork.svg';
import { Avatar } from 'components/Avatar';
import moment from 'moment';
import MinorAgeChecker from 'components/MinorAgeChecker';
import { GetWorkersArgs, ResponseGetWorkerType } from 'redux/Api/ordersApi/type';
import { EmployedsFilterParams, Filters } from 'pages/EmployedsContent/Filters/Filters';
import { useGetFiltersEmployedsQuery } from 'redux/Api/employedsApi/employedsApi';
import { useMediaQuery, useElementSize } from '@mantine/hooks';
import { MobileMenu } from 'pages/EmployedsContent/Sort/SortFilterMobile';
import ContentLoader from 'react-content-loader';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useGetProfileQuery } from 'redux/Api/profileApi/ProfileApi';
import { AppTooltip } from 'ui/AppTooltip';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { HandleFieldKey, HandleFieldValue } from 'components/CreateOrderForm/types';
import { FormatAmount } from 'ui/FormatAmount';
import { enumerate } from 'utils/strActions';
import { formatEndDate } from 'utils/date';

type SearchParamsState = GetWorkersArgs | undefined;

const mobileMenuFiltersOnlyStub = () => undefined;

interface EmployeeSelectionProps {
  workers: ResponseGetWorkerType[] | undefined;
  selectedWorkerId: number | null;
  onWorkerSelect: (id: number | null) => void;
  error?: string | null;
  setSearachParams?: React.Dispatch<React.SetStateAction<SearchParamsState>>;
  workersFetching?: boolean;
  workersLoading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  handleFieldChange: (key: HandleFieldKey, value: HandleFieldValue) => void;
}

export const EmployeeSelection: React.FC<EmployeeSelectionProps> = ({
  workers,
  selectedWorkerId,
  onWorkerSelect,
  error,
  setSearachParams,
  workersFetching,
  workersLoading,
  hasNextPage,
  onLoadMore,
  handleFieldChange,
}) => {
  const { infoOrderDetail } = useSelector((state: RootState) => state.ordersDetailsSlice);
  const [searchValue, setSearchValue] = useState('');
  const { data: dataFilters } = useGetFiltersEmployedsQuery();
  const [filtersParams, setFiltersParams] = useState<EmployedsFilterParams>({
    genders: [],
    issues: [],
    nationalities: [],
    categories: [],
    contracts: [],
    limit_from: undefined,
    limit_to: undefined,
    date_add: undefined,
  });

  const { data: profileData } = useGetProfileQuery();
  const { ref, width } = useElementSize();

  useEffect(() => {
    if (!profileData) return;
    if (!profileData.features?.marketplace) {
      handleFieldChange('is_marketplace', false);
    }
  }, [profileData, handleFieldChange]);

  const isMediumWidth =
    useMediaQuery('(min-width: 1200px) and (max-width: 1406px)') ?? false;
  const isSmallWidth = useMediaQuery('(max-width: 767px)') ?? false;

  const dataFiltersMobile = useMemo(() => {
    const f = dataFilters?.filters;
    if (!f) return undefined;
    const rows = (items: { keyword: string; title: string }[]) =>
      items?.map((item, i) => ({
        id: i + 1,
        keyword: item.keyword,
        label: item.title,
      }));
    return {
      genders: rows(f.genders),
      issues: rows(f.issues),
      nationalities: rows(f.nationalities),
      contracts: rows(f.contracts),
      categories: rows(f.categories),
    };
  }, [dataFilters]);

  const applySearchParams = (next: SearchParamsState) => {
    if (!setSearachParams) return;
    setSearachParams(next);
  };

  const [intersectionRef, { rootRef: tableScrollRootRef }] = useInfiniteScroll({
    loading: Boolean(workersLoading),
    hasNextPage: Boolean(hasNextPage),
    onLoadMore: () => onLoadMore?.(),
    rootMargin: '0px 0px 160px 0px',
    disabled: !onLoadMore,
  });

  const convertFiltersToApiParams = (filters: EmployedsFilterParams) => {
    return {
      genders: filters.genders,
      issues: filters.issues,
      categories: filters.categories?.map((c) => c.id),
      nationalities: filters.nationalities,
      contracts: filters.contracts,
      limit_from: filters.limit_from,
      limit_to: filters.limit_to,
      date_add: filters.date_add,
    };
  };

  const buildMergedParams = (nextSearch: string, nextFilters: EmployedsFilterParams) => {
    const hasSearch = Boolean(nextSearch.trim());
    const hasAnyFilters =
      (nextFilters.genders?.length ?? 0) > 0 ||
      (nextFilters.issues?.length ?? 0) > 0 ||
      (nextFilters.nationalities?.length ?? 0) > 0 ||
      (nextFilters.categories?.length ?? 0) > 0 ||
      (nextFilters.contracts?.length ?? 0) > 0 ||
      nextFilters.limit_from != null ||
      nextFilters.limit_to != null ||
      nextFilters.date_add?.start != null ||
      nextFilters.date_add?.end != null;

    if (!hasSearch && !hasAnyFilters) return undefined;

    const apiFilters = convertFiltersToApiParams(nextFilters);

    return {
      ...apiFilters,
      ...(hasSearch ? { search: nextSearch.trim() } : {}),
    };
  };

  const handleToggleSelling = () => {
    handleFieldChange('is_marketplace', !infoOrderDetail.is_marketplace);
  };

  const selectedWorker = workers?.find(
    (worker: ResponseGetWorkerType) => worker.id === selectedWorkerId,
  );

  useEffect(() => {
    if (workersLoading || workersFetching) return;
    if (!buildMergedParams(searchValue, filtersParams)) return;
    if (!selectedWorkerId) return;
    if (!selectedWorker) {
      onWorkerSelect(null);
    }
  }, [
    selectedWorker,
    selectedWorkerId,
    workersLoading,
    workersFetching,
    searchValue,
    filtersParams,
    onWorkerSelect,
  ]);

  useEffect(() => {
    if (selectedWorkerId) {
      handleFieldChange('is_marketplace', false);
    }
  }, [selectedWorkerId, handleFieldChange]);

  const isHasSignedContract =
    selectedWorker?.unified_contract?.status === 'signed_by_worker';

  const handleShowLastOrder = (id: number, name: string) => {
    localStorage.setItem(
      'ordersFilters',
      JSON.stringify({
        type_ids: [],
        payment_type_ids: [],
        customer_ids: [],
        worker_ids: [{ id: id.toString(), name }],
        preset_ids: [],
        statuses: [],
        amount_from: undefined,
        amount_to: undefined,
        numbers_range: undefined,
        date_start_range: undefined,
        date_end_range: undefined,
        create_date_range: undefined,
        date_paid_range: {
          start: moment().subtract(3, 'months').format('YYYY-MM-DD'),
          end: moment().format('YYYY-MM-DD'),
        },
        act: undefined,
        receipts: undefined,
        fns_receipts_seen_status: undefined,
      }),
    );
    window.open('/orders', '_blank', 'noopener,noreferrer');
  };

  return (
    <div ref={ref} className={styles.container}>
      <div className={styles.card}>
        <div className={styles.content}>
          <div className={styles.header}>
            <p className={styles.title}>Исполнители</p>
            {selectedWorkerId && (
              <Button theme='link' icon src={close} onClick={() => onWorkerSelect(null)}>
                Сбросить
              </Button>
            )}
          </div>
          {profileData?.features?.marketplace && !selectedWorkerId && (
            <Group gap='8px' h='40px'>
              <Switch
                color='#002CFB'
                styles={{
                  label: {
                    fontSize: '16px',
                    lineHeight: '22px',
                    color: '#121922',
                    cursor: 'pointer',
                  },
                  track: {
                    border: 'none',
                    cursor: 'pointer',
                    backgroundColor: infoOrderDetail.is_marketplace
                      ? '#002CFB'
                      : 'rgba(173, 191, 223, 0.40)',
                  },
                  thumb: {
                    border: 'none',
                  },
                }}
                checked={infoOrderDetail.is_marketplace}
                onChange={() => {
                  handleToggleSelling();
                }}
                label='Опубликовать для всех'
              />
              <AppTooltip label='Вы будете получать отклики на заказ и сможете выбрать подходящего исполнителя'>
                <InfoIcon style={{ cursor: 'pointer' }} />
              </AppTooltip>
            </Group>
          )}
          {!infoOrderDetail.is_marketplace && (
            <>
              {selectedWorkerId ? (
                <div className={styles.selected}>
                  <div className={styles.employee_details}>
                    <div className={styles.employee_header}>
                      <Avatar
                        name={selectedWorker?.name}
                        surname={selectedWorker?.surname}
                        avatar={selectedWorker?.worker_app?.avatar ?? null}
                        id={selectedWorker?.id}
                        statusActive={''}
                        height='82px'
                        width='82px'
                        isDeleted={selectedWorker?.is_deleted}
                      />
                      <div className={styles.employee_info}>
                        <div className={styles.employee_name_wrapper}>
                          <h3 className={styles.employee_name}>
                            {selectedWorker?.surname} {selectedWorker?.name}{' '}
                            {selectedWorker?.patronymic}
                          </h3>
                          <p className={styles.employee_phone}>
                            {getFormatPhone(selectedWorker?.phone)}
                          </p>
                        </div>
                        <p className={styles.employee_inn}>ИНН {selectedWorker?.inn}</p>
                        <p className={styles.risk_level}>Риск минимальный</p>
                      </div>
                    </div>

                    <div className={styles.status_indicators}>
                      {isHasSignedContract && (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <CheckIcon />
                          </div>
                          <p>Договор № УД-{selectedWorker?.unified_contract?.number}</p>
                        </div>
                      )}
                      {isHasSignedContract && (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <CheckIcon />
                          </div>
                          <p>
                            Лимит по договору:{' '}
                            {selectedWorker?.unified_contract?.limit
                              ? `${
                                  selectedWorker?.unified_contract?.total_orders_amount?.toLocaleString(
                                    'ru-RU',
                                  ) || 0
                                } ₽ из ${selectedWorker?.unified_contract?.limit?.toLocaleString(
                                  'ru-RU',
                                )} ₽`
                              : 'Не указан'}
                          </p>
                        </div>
                      )}
                      {selectedWorker?.limit && (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <CheckIcon />
                          </div>
                          <p>
                            Лимит на месяц:{' '}
                            {selectedWorker?.monthly_payment?.toLocaleString('ru-RU') ||
                              '0'}{' '}
                            ₽ из {selectedWorker?.limit?.toLocaleString('ru-RU')} ₽
                          </p>
                        </div>
                      )}
                      {!selectedWorker?.worker_app?.nationality ? (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <AlertIcon />
                          </div>
                          <p>Гражданство неизвестно. Обратитесь в поддержку</p>
                        </div>
                      ) : selectedWorker?.worker_app?.nationality !== 'Россия' ? (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <AlertIcon />
                          </div>
                          <p>
                            Не является гражданином России. Не забудь отправить
                            уведомление в МВД
                          </p>
                        </div>
                      ) : null}
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <CheckIcon />
                        </div>
                        <p>Является самозанятым</p>
                      </div>
                      {Boolean(selectedWorker?.has_ip) && (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <WarningIcon />
                          </div>
                          <p>Есть открытое ИП</p>
                        </div>
                      )}
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <CheckIcon />
                        </div>
                        <p>Не состоит в списке экстремистов и террористов</p>
                      </div>
                      <div className={styles.status_item}>
                        <div className={styles.status_icon}>
                          <CheckIcon />
                        </div>
                        <p>Лимит дохода НПД 2,4 млн. не израсходован</p>
                      </div>
                      {selectedWorker?.is_deleted && (
                        <div className={styles.status_item}>
                          <div className={styles.status_icon}>
                            <IconError style={{ width: '24px', height: '24px' }} />
                          </div>
                          <p style={{ color: '#e10d0d' }}>Профиль удален</p>
                        </div>
                      )}
                      <div>
                        <MinorAgeChecker data={selectedWorker} showLabel={false} />
                      </div>
                    </div>

                    <p className={styles.report_date}>
                      Отчет на {moment().format('D MMMM YYYY')}
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <div className={error ? styles['error-border'] : ''}>
                    <div className={styles.searchLine}>
                      <div style={{ width: '100%', minWidth: 0 }}>
                        <InputSearch
                          notFocus
                          value={searchValue}
                          placeholder='Искать по ИНН, ФИО или номеру телефона'
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setSearchValue(e.target.value);
                            if (!e.target.value.trim() && setSearachParams) {
                              applySearchParams(buildMergedParams('', filtersParams));
                            }
                          }}
                          onSearch={() => {
                            applySearchParams(
                              buildMergedParams(searchValue, filtersParams),
                            );
                          }}
                        />
                      </div>
                      <div className={styles.onlyMobile}>
                        <MobileMenu
                          filtersOnly
                          filtersOnlyIconOnly
                          mobileTabsSort={[]}
                          handleTabClickSort={mobileMenuFiltersOnlyStub}
                          activeSortName='default'
                          setActiveSort={mobileMenuFiltersOnlyStub}
                          dataFiltersMobile={dataFiltersMobile}
                          initialFilters={{
                            genders: filtersParams.genders ?? [],
                            issues: filtersParams.issues ?? [],
                            nationalities: filtersParams.nationalities ?? [],
                            categories: filtersParams.categories ?? [],
                            contracts: filtersParams.contracts ?? [],
                            limit_from: filtersParams.limit_from,
                            limit_to: filtersParams.limit_to,
                            date_add: filtersParams.date_add,
                          }}
                          showFilters={true}
                          onApplyFilters={(nextFilters) => {
                            setFiltersParams({
                              genders: nextFilters.genders ?? [],
                              issues: nextFilters.issues ?? [],
                              nationalities: nextFilters.nationalities ?? [],
                              categories: nextFilters.categories ?? [],
                              contracts: nextFilters.contracts ?? [],
                              limit_from: nextFilters.limit_from,
                              limit_to: nextFilters.limit_to,
                              date_add: nextFilters.date_add,
                            });
                            applySearchParams(
                              buildMergedParams(searchValue, nextFilters),
                            );
                          }}
                          onResetFilters={() => {
                            const empty: EmployedsFilterParams = {
                              genders: [],
                              issues: [],
                              nationalities: [],
                              categories: [],
                              contracts: [],
                              limit_from: undefined,
                              limit_to: undefined,
                              date_add: undefined,
                            };
                            setFiltersParams(empty);
                            applySearchParams(buildMergedParams(searchValue, empty));
                          }}
                          mobileTabs={[]}
                          handleTabClick={mobileMenuFiltersOnlyStub}
                          activeTabName=''
                          toggleSearchOpen={mobileMenuFiltersOnlyStub}
                        />
                      </div>
                      <div className={styles.onlyDesktop}>
                        <Filters
                          dataFilters={
                            dataFilters?.filters ?? {
                              genders: [],
                              issues: [],
                              categories: [],
                              contracts: [],
                              nationalities: [],
                            }
                          }
                          notShowText={isMediumWidth || isSmallWidth}
                          showFilters={true}
                          initialFilters={{
                            genders: filtersParams.genders ?? [],
                            issues: filtersParams.issues ?? [],
                            nationalities: filtersParams.nationalities ?? [],
                            categories: filtersParams.categories ?? [],
                            contracts: filtersParams.contracts ?? [],
                            limit_from: filtersParams.limit_from,
                            limit_to: filtersParams.limit_to,
                            date_add: filtersParams.date_add,
                          }}
                          onApplyFilters={(nextFilters) => {
                            setFiltersParams({
                              genders: nextFilters.genders ?? [],
                              issues: nextFilters.issues ?? [],
                              nationalities: nextFilters.nationalities ?? [],
                              categories: nextFilters.categories ?? [],
                              contracts: nextFilters.contracts ?? [],
                              limit_from: nextFilters.limit_from,
                              limit_to: nextFilters.limit_to,
                              date_add: nextFilters.date_add,
                            });
                            applySearchParams(
                              buildMergedParams(searchValue, nextFilters),
                            );
                          }}
                          onResetFilters={() => {
                            const empty: EmployedsFilterParams = {
                              genders: [],
                              issues: [],
                              nationalities: [],
                              categories: [],
                              contracts: [],
                              limit_from: undefined,
                              limit_to: undefined,
                              date_add: undefined,
                            };
                            setFiltersParams(empty);
                            applySearchParams(buildMergedParams(searchValue, empty));
                          }}
                        />
                      </div>
                    </div>
                    {width >= 700 && (
                      <div className={styles.tableWrapper}>
                        <p style={{ textAlign: 'left' }}>Исполнители</p>
                        <p style={{ textAlign: 'center' }}>Последний заказ, ₽</p>
                        <p style={{ textAlign: 'right' }}>Последние 3 мес, ₽</p>
                      </div>
                    )}
                    <div className={styles.table} ref={tableScrollRootRef}>
                      {workersLoading ? (
                        <ContentLoader
                          speed={2}
                          height={'270px'}
                          width='100%'
                          className={styles.tab_loader}
                          backgroundColor={'#FFFFFF'}
                          foregroundColor={'#D8E4FB8C'}>
                          <rect x='0' y='0' rx='0' ry='0' width='100%' height='100%' />
                        </ContentLoader>
                      ) : (
                        <>
                          {workers?.length ? (
                            <>
                              {workers.map((worker: ResponseGetWorkerType) => (
                                <div
                                  key={worker.id}
                                  className={styles.row}
                                  onClick={() => onWorkerSelect(worker.id)}>
                                  {width >= 700 && (
                                    <div className={`${styles.user} ${styles.desktop}`}>
                                      <div className={styles.userInfo}>
                                        <InfoEmployeds
                                          status={worker.worker_app?.app_status}
                                          avatar={worker.worker_app?.avatar ?? null}
                                          id={worker.id}
                                          name={worker.name}
                                          surname={worker.surname}
                                          patronymic={worker.patronymic}
                                          phone={getFormatPhone(worker.phone)}
                                          nationality={
                                            worker.worker_app?.nationality ?? ''
                                          }
                                          birthdate={worker.worker_app?.birthdate ?? ''}
                                        />
                                        <div className={styles.lastOrder}>
                                          {worker.last_order_amount ? (
                                            <div>
                                              {FormatAmount({
                                                amount:
                                                  worker.last_order_amount.toString(),
                                                compact: true,
                                              })}
                                            </div>
                                          ) : (
                                            ''
                                          )}

                                          {worker.last_order_date && (
                                            <p style={{ paddingRight: '8px' }}>
                                              {formatEndDate(worker.last_order_date)}
                                            </p>
                                          )}
                                        </div>
                                        <div className={styles.last3Months}>
                                          {worker.last_3_months_orders_count ? (
                                            <div
                                              className={styles.last3MonthsCount}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleShowLastOrder(
                                                  worker.id,
                                                  `${worker.surname} ${worker.name} ${worker.patronymic}`,
                                                );
                                              }}>
                                              {worker.last_3_months_orders_count}{' '}
                                              {enumerate(
                                                worker.last_3_months_orders_count,
                                                ['заказ', 'заказа', 'заказов'],
                                              )}
                                            </div>
                                          ) : (
                                            ''
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {width < 700 && (
                                    <div className={styles.mobile}>
                                      <div className={styles.userInfoMobile}>
                                        <InfoEmployeds
                                          status={worker.worker_app?.app_status}
                                          avatar={worker.worker_app?.avatar ?? null}
                                          id={worker.id}
                                          name={worker.name}
                                          surname={worker.surname}
                                          patronymic={worker.patronymic}
                                          phone={worker.phone ?? undefined}
                                          withPhoneIcon={false}
                                          birthdate={worker.worker_app.birthdate ?? ''}
                                          nationality={
                                            worker.worker_app.nationality ?? ''
                                          }
                                        />
                                      </div>
                                      <div className={styles.lastOrderMobile}>
                                        <IconWork />
                                        <div className={styles.lastOrderMobileInfo}>
                                          Последний заказ, ₽
                                          {worker.last_order_date && (
                                            <p>{formatEndDate(worker.last_order_date)}</p>
                                          )}
                                        </div>
                                        {worker.last_order_amount ? (
                                          <div className={styles.lastOrderMobileValue}>
                                            {FormatAmount({
                                              amount: worker.last_order_amount.toString(),
                                              compact: true,
                                            })}
                                          </div>
                                        ) : null}
                                      </div>
                                      <div className={styles.lastOrderMobile}>
                                        <IconCalendar />
                                        <div className={styles.lastOrderMobileInfo}>
                                          Последние 3 мес
                                        </div>
                                        {worker.last_3_months_orders_count ? (
                                          <div
                                            className={styles.last3MonthsCount}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleShowLastOrder(
                                                worker.id,
                                                `${worker.surname} ${worker.name} ${worker.patronymic}`,
                                              );
                                            }}>
                                            {worker.last_3_months_orders_count}{' '}
                                            {enumerate(
                                              worker.last_3_months_orders_count,
                                              ['заказ', 'заказа', 'заказов'],
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {workersFetching && (
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: '10px 0',
                                  }}>
                                  <Loader size='sm' color='#002cfb' />
                                </div>
                              )}
                            </>
                          ) : (
                            <div className={styles.empty}>Нет исполнителей</div>
                          )}
                          {hasNextPage ? (
                            <div ref={intersectionRef} style={{ height: 10 }} />
                          ) : null}
                        </>
                      )}
                    </div>
                  </div>

                  {error && <p className={styles['error-info']}>{error}</p>}
                </div>
              )}
            </>
          )}
        </div>
        {!selectedWorkerId && !infoOrderDetail.is_marketplace && (
          <p className={styles.info}>
            Предложить заказ можно только готовым к сотрудничеству исполнителям
          </p>
        )}
      </div>
    </div>
  );
};
